'use client';

import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { generateBreadcrumbJsonLd, Breadcrumb } from './Breadcrumb';
import Head from 'next/head';
import dynamic from 'next/dynamic';

// Dynamic import for JsonLd component
const JsonLd = dynamic(() => import('./JsonLd'), { ssr: true });

function splitTitleIntoSpans(titleElement: HTMLHeadingElement) {
  const text = titleElement.textContent || '';
  titleElement.textContent = '';

  for (const char of text) {
    const span = document.createElement('span');
    span.className = 'inline-block';
    span.textContent = char === ' ' ? '\u00A0' : char;
    titleElement.appendChild(span);
  }
}

interface Breadcrumb {
  label: string;
  url: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  theme?: 'light' | 'dark' | 'primary' | 'pink';
  size?: 'small' | 'medium' | 'large';
  breadcrumbs?: Breadcrumb[];
  backgroundImage?: string;
  align?: 'left' | 'center' | 'right';
  fontStyle?: 'normal' | 'serif' | 'display' | 'slab';
  animation?: 'stagger' | 'wave' | 'reveal' | 'fade';
  seoTitle?: string; // SEO özel başlık
  canonicalUrl?: string; // Canonical URL
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  theme = 'light',
  size = 'medium',
  breadcrumbs,
  backgroundImage,
  align = 'center',
  fontStyle = 'normal',
  animation = 'stagger',
  seoTitle,
  canonicalUrl,
}) => {
  // Animasyon için referanslar
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const decorRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  
  // Size sınıflarını belirle
  const sizeClasses = {
    small: 'py-6 md:py-8',
    medium: 'py-8 md:py-12',
    large: 'py-12 md:py-16',
  };

  // Hizalama sınıflarını belirle
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  // Tema sınıflarını belirle
  const themeClasses = {
    light: 'text-gray-800',
    dark: 'text-gray-800',
    primary: 'text-gray-800',
    pink: 'text-gray-800',
  };

  // Font stillerini belirle
  const fontStyleClasses = {
    normal: 'font-sans',
    serif: 'font-serif',
    display: 'font-display',
    slab: 'font-mono',
  };

  const containerClasses = cn(
    'relative overflow-hidden',
    sizeClasses[size],
    alignClasses[align],
    themeClasses[theme],
    fontStyleClasses[fontStyle],
    {
      'bg-cover bg-center': !!backgroundImage,
    }
  );

  const titleClasses = cn(
    'font-bold leading-tight',
    {
      'text-2xl md:text-3xl': size === 'small',
      'text-3xl md:text-4xl lg:text-5xl': size === 'medium',
      'text-4xl md:text-5xl lg:text-6xl': size === 'large',
      'mb-2': !!subtitle,
      'mb-0': !subtitle,
    }
  );

  const subtitleClasses = cn(
    'max-w-xl mx-auto',
    {
      'text-gray-800': theme === 'light' || theme === 'dark' || theme === 'primary' || theme === 'pink',
      'text-sm md:text-base': size === 'small',
      'text-base md:text-lg': size === 'medium',
      'text-lg md:text-xl': size === 'large',
      'mx-0': align !== 'center',
    }
  );

  // Animasyon efekti
  useEffect(() => {
    if (!titleRef.current) return;

    // Sayfa yüklendiğinde animasyonu başlat
    const animationDelay = 300; // ms
    
    // Temel animasyon timeline'ı
    const timeline = anime.timeline({
      easing: 'easeOutExpo',
      duration: 800
    });

    // Badge animasyonu (varsa)
    if (badgeRef.current) {
      timeline.add({
        targets: badgeRef.current,
        translateY: [-20, 0],
        opacity: [0, 1],
        duration: 600,
        easing: 'easeOutQuad'
      }, 0);
    }

    // Container giriş animasyonu
    timeline.add({
      targets: containerRef.current,
      opacity: [0, 1],
      duration: 600,
      easing: 'easeInOutQuad'
    }, 0);

    // Dekoratif elementin animasyonu
    if (decorRef.current) {
      timeline.add({
        targets: decorRef.current,
        width: [0, '100%'],
        easing: 'easeInOutQuad',
        duration: 600
      }, '-=400');
    }

    // Farklı animasyon tipleri
    if (animation === 'stagger') {
      // Başlık animasyonu - Stagger
      // Başlığı harflere böl
      if (titleRef.current && titleRef.current.textContent) {
        splitTitleIntoSpans(titleRef.current);
        
        timeline.add({
          targets: titleRef.current.querySelectorAll('span'),
          translateY: [40, 0],
          translateZ: 0,
          opacity: [0, 1],
          easing: 'easeOutExpo',
          duration: 1200,
          delay: anime.stagger(20)
        }, '-=400');
      }
    } else if (animation === 'wave') {
      // Dalga animasyonu
      if (titleRef.current && titleRef.current.textContent) {
        splitTitleIntoSpans(titleRef.current);
        
        timeline.add({
          targets: titleRef.current.querySelectorAll('span'),
          translateY: function(el: HTMLElement, i: number) {
            return [0, -15 * Math.sin(i * 0.3)];
          },
          translateX: function(el: HTMLElement, i: number) {
            return [0, 5 * Math.sin(i * 0.3)];
          },
          opacity: [0, 1],
          easing: 'easeOutElastic(1, .6)',
          duration: 1000,
          delay: anime.stagger(40)
        }, '-=400');
      }
    } else if (animation === 'reveal') {
      // Keşif animasyonu
      timeline.add({
        targets: titleRef.current,
        clipPath: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'],
        opacity: [0, 1],
        easing: 'easeInOutQuint',
        duration: 1200
      }, '-=400');
    } else {
      // Fade animasyonu
      timeline.add({
        targets: titleRef.current,
        opacity: [0, 1],
        translateY: [20, 0],
        easing: 'easeOutQuad',
        duration: 800
      }, '-=400');
    }

    // Alt başlık animasyonu
    if (subtitleRef.current) {
      timeline.add({
        targets: subtitleRef.current,
        opacity: [0, 1],
        translateY: [20, 0],
        easing: 'easeOutExpo',
        duration: 800
      }, '-=600');
    }

    return () => {
      // Temizleme işlemi
      anime.remove([
        containerRef.current, 
        titleRef.current, 
        subtitleRef.current, 
        decorRef.current,
        badgeRef.current
      ]);
    };
  }, [title, subtitle, animation]);

  // Breadcrumb formatını dönüştüren yardımcı fonksiyon
  const formatBreadcrumbs = (breadcrumbs: Breadcrumb[]) => {
    return breadcrumbs.map(item => ({
      label: item.label,
      href: item.url
    }));
  };

  return (
    <>
      {/* Structured data for SEO - JSON-LD */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <JsonLd schema={generateBreadcrumbJsonLd(formatBreadcrumbs(breadcrumbs))} />
      )}
      
      <div
        ref={containerRef}
        className={containerClasses}
        style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}}
      >
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className={`mb-4 ${align === 'center' ? 'flex justify-center' : ''}`}>
            <Breadcrumb items={formatBreadcrumbs(breadcrumbs)} />
          </div>
        )}
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Badge - Kaldırıldı */}
          
          {/* Başlık - tema ve fontStyle'a göre farklılaştırılmış */}
          <h1 ref={titleRef} className={titleClasses}>{title}</h1>
          
          {/* Alt Başlık */}
          {subtitle && <p ref={subtitleRef} className={subtitleClasses}>{subtitle}</p>}
          
          {/* Dekoratif çizgi - sadece belirli temalarda ve serif yazı tipinde göster */}
          {fontStyle === 'serif' && theme !== 'light' && (
            <div 
              ref={decorRef}
              className={`mt-6 h-1 rounded-full w-20 opacity-70 ${
                theme === 'pink' ? 'bg-primary' : theme === 'primary' ? 'bg-primary-light' : 'bg-white'
              } ${align === 'center' ? 'mx-auto' : align === 'right' ? 'ml-auto' : ''}`}
            ></div>
          )}
        </div>
      </div>
    </>
  );
};

export default PageHeader;